/* tslint:disable:max-line-length */
import {Injectable} from '@angular/core';
import {FieldDataTypeEnum} from '@docma-mm-core/field/field-data-type.enum';
import {ColDef, ColumnState, DragStoppedEvent, GridOptions, ValueFormatterParams} from 'ag-grid-community';
import {
  AttributeGroupColDef,
  BooleanColDef,
  DateColDef,
  DelayColDef,
  ImageColDef,
  NumberColDef,
  PositiveIntegerColDef,
  SalutationColDef,
  StatusGroupColDef,
  TextColDef,
  TimeColDef
} from './col-defs';
import {deepMerge} from '@mydocma/common/src/lib/deep';
import {LayoutField} from '@docma-mm-core/layout/layout-field';
import {FieldOrder, LayoutStateModel} from '../../../mm-filter/layout/layout-state.model';
import {MmLayoutStateService} from '../../../mm-filter/layout/layout-state.service';
import {filter, firstValueFrom, map, timer} from 'rxjs';
import {MydocmaDatePipe, MydocmaNumberPipe} from '@mydocma/common';
import {SyncCellRendererComponent} from '../cell-renderers/sync.cell-renderer/sync.cell-renderer.component';
import {SortChangedEvent} from 'ag-grid-community/dist/lib/events';
import {LayoutSortItem} from '@docma-mm-core/layout/layout-sort-item';
import {SortDirectionEnum} from '@docma-mm-core/sorting/sort-direction.enum';
import {UntilDestroy} from '@ngneat/until-destroy';
import {Layout} from '@docma-mm-core/layout/layout';
import {Field} from '@docma-mm-core/field/field';
import {colTypeMinWidth} from './field-defaults';
import {FaultSortingSourceEnum} from '@docma-mm-core/fault/fault-sorting-source.enum';
import {
  GridGroupColumn,
  GroupSortType,
  MydocmaColumnDefinition,
  MydocmaGridOptions,
  SelectionCheckboxCellRenderer,
  SelectionCheckboxHeaderCellRenderer
} from '@mydocma/component-library/src/lib/grid';
import {FieldDefaultNameEnum} from '@docma-mm-core/field/field-default-name.enum';
import {MmTranslateService} from '../../../mm-translate.service';
import {getLocaleNumberSymbol, NumberSymbol} from '@angular/common';
import {FilterService} from '../filters/filter.service';
import {MmFaultsSortingService} from '../../mm-faults-view/services/mm-faults-sorting.service';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import { MmProjectLoaderService } from '../../../mm-project/mm-project-loader.service';

@UntilDestroy()
@Injectable()
export class ColDefsService {

  public imageColumnActive = false;
  public currentColDefs: ColDef[];
  private gridOptions: MydocmaGridOptions;
  private layoutState: LayoutStateModel;

  constructor(private readonly datepipe: MydocmaDatePipe,
              private readonly numberpipe: MydocmaNumberPipe,
              private readonly layoutStateService: MmLayoutStateService,
              private readonly translateService: MmTranslateService,
              public readonly filterService: FilterService,
              private readonly sortingService: MmFaultsSortingService,
              private readonly projectLoaderService: MmProjectLoaderService) {
    this.layoutStateService.filterService = filterService;
  }

  get currentLayoutState() {
    return this.layoutState;
  }

  public attach(gridOptions: GridOptions) {
    this.gridOptions = gridOptions as MydocmaGridOptions;
    if (this.gridOptions) {
      this.gridOptions.onColumnResized = this.onColumnResized.bind(this);
      this.gridOptions.onSortChanged = this.onColumnSorted.bind(this);
      this.gridOptions.onDragStopped = this.onColumnDragged.bind(this);
    }
  }

  public async updateUserState(groupColumns: GridGroupColumn[]) {
    const sortedColumns: { [defaultName: string]: ColumnState } = {};
    this.gridOptions.columnApi.getColumnState().filter(col => col.sortIndex != null).map(col => sortedColumns[col.colId] = col);

    const groupedColumns: { [defaultName: string]: { field: string, groupSort: GroupSortType, sortIndex: number } } = {};
    let i = 0;
    const gridGroupService = this.gridOptions?.mydocmaApi?.groupService;

    if (groupColumns) {
      groupColumns?.forEach(grp => {
        groupedColumns[grp.group.colId] = {field: grp.group.colId, groupSort: (grp.group.groupSort ?? (sortedColumns.hasOwnProperty(grp.group.colId) ? sortedColumns[grp.group.colId].sort as GroupSortType : GroupSortType.asc)), sortIndex: ++i};
      });

    }
    else if (gridGroupService && !groupColumns) {
      gridGroupService.currentGroupByColumns?.forEach(grp => {
        groupedColumns[grp.colId] = {field: grp.colId, groupSort: (grp.groupSort ?? (sortedColumns.hasOwnProperty(grp.colId) ? sortedColumns[grp.colId].sort as GroupSortType : GroupSortType.asc)), sortIndex: ++i};
      });
    }

    const userState = (!this.layoutState?.userState ? new Layout() : JSON.parse(JSON.stringify(this.layoutState.userState)));

    if (!userState.Fields) {
      userState.Fields = [];
    }

    const _unsortedFieldsAdd = (this.layoutState?.layout.Fields ?? []).filter(lf => {
        return ((lf.Sorting !== 0 && !sortedColumns.hasOwnProperty(lf.Field.DefaultName))
              || ((this.layoutState?.userState?.Fields?.find(usf => usf.Field.Id === lf.Field.Id)?.Sorting ?? 0) !== 0 && !sortedColumns.hasOwnProperty(lf.Field.DefaultName)))
              && !userState.Fields.some(usf => usf.Field.Id === lf.Field.Id && (usf.Grouping ?? 0) !== 0)
    });

    const _sortedFieldsUpdate = (this.layoutState?.layout.Fields ?? []).filter(lf => {
        return ((lf.Sorting !== 0 && sortedColumns.hasOwnProperty(lf.Field.DefaultName))
                || ((this.layoutState?.userState?.Fields?.find(usf => usf.Field.Id === lf.Field.Id)?.Sorting ?? 0) !== 0 && sortedColumns.hasOwnProperty(lf.Field.DefaultName)))
                && !userState.Fields.some(usf => usf.Field.Id === lf.Field.Id && (usf.Grouping ?? 0) !== 0);
    });

    const _sortedFieldsAdd = Object.keys(sortedColumns).filter(defaultName => {
        const field = (this.layoutState?.layout.Fields ?? []).find(x => x.Field.DefaultName === defaultName);
        return !(this.layoutState?.layout.Fields ?? []).some(lf => lf.Sorting !== 0 && lf.Field.DefaultName === defaultName)
                && !userState.Fields.some(usf => usf.Field.Id === field.Id && (usf.Grouping ?? 0) !== 0);
    });


    _unsortedFieldsAdd.forEach(f => {
      const usLf = userState.Fields.find(x => x.Field.Id === f.Field.Id);
      if (usLf) {
        if (!!usLf.Width || f.Sorting !== 0) {
          usLf.Sorting = 0;
          usLf.IsDescending = false;
        }
        else {
          const i = userState.Fields.findIndex(y => y.Field.Id === f.Field.Id);
          userState.Fields.splice(i, 1);
        }
      }
      else {
        const lf = new LayoutField({Field: new Field({Id: f.Field.Id}), Sorting: 0, IsDescending: false});
        userState.Fields.push(lf)
      }
    });

    _sortedFieldsUpdate.forEach(f => {
      const colState = sortedColumns[f.Field.DefaultName];
      const usLf = userState.Fields.find(x => x.Field.Id === f.Field.Id);
      if (f.Sorting !== (colState.sortIndex + 1) || f.IsDescending !== (colState.sort === 'desc' ? true : false) || f.Sorting !== usLf?.Sorting || f.IsDescending !== usLf?.IsDescending) {
        if (usLf) {
          if (!!usLf.Width || f.Sorting !== (colState.sortIndex + 1) || f.IsDescending !== (colState.sort === 'desc' ? true : false)) {
            usLf.Sorting = (colState.sortIndex + 1);
            usLf.IsDescending = (colState.sort === 'desc' ? true : false);
          }
          else {
            const i = userState.Fields.findIndex(y => y.Field.Id === f.Field.Id);
            userState.Fields.splice(i, 1);
          }
        }
        else {
          const lf = new LayoutField({
            Field: new Field({Id: f.Field.Id}),
            Sorting: (colState.sortIndex + 1),
            IsDescending: (colState.sort === 'desc' ? true : false)
          });
          userState.Fields.push(lf);
        }
      }
    });

    _sortedFieldsAdd.forEach(defaultName => {
      const colState = sortedColumns[defaultName];
      const field = (this.layoutState?.layout.Fields ?? []).find(x => x.Field.DefaultName === defaultName);
      const usLf = userState.Fields.find(x => x.Field.Id === field.Field.Id);
      if (usLf) {
        usLf.Sorting = (colState.sortIndex + 1);
        usLf.IsDescending = (colState.sort === "desc" ? true : false);
      }
      else {
        userState.Fields.push(new LayoutField({
          Field: new Field({Id: field.Field.Id}),
          Sorting: (colState.sortIndex + 1),
          IsDescending: (colState.sort === 'desc' ? true : false)
        }));
      }
    });


    const _ungroupedFieldsAdd = (this.layoutState?.layout.Fields ?? []).filter(lf => {
      return (lf.Grouping !== 0 && !groupedColumns.hasOwnProperty(lf.Field.DefaultName))
        || ((this.layoutState?.userState?.Fields?.find(usf => usf.Field.Id === lf.Field.Id)?.Grouping ?? 0) !== 0 && !groupedColumns.hasOwnProperty(lf.Field.DefaultName));

    });

    const _groupedFieldsUpdate = (this.layoutState?.layout.Fields ?? []).filter(lf => {
        return (lf.Grouping !== 0 && groupedColumns.hasOwnProperty(lf.Field.DefaultName))
          || (this.layoutState?.userState?.Fields && !this.layoutState.userState.Fields?.some(usf => usf.Field.Id === lf.Field.Id && (usf.Grouping ?? 0) !== 0) && groupedColumns.hasOwnProperty(lf.Field.DefaultName));
    });

    const _groupedFieldsAdd = Object.keys(groupedColumns).filter(defaultName =>
      !(this.layoutState?.layout.Fields ?? []).some(lf => lf.Grouping !== 0 && lf.Field.DefaultName === defaultName)
    );

    _ungroupedFieldsAdd.forEach(f => {
      const usLf = userState.Fields.find(x => x.Field.Id === f.Field.Id);

      if (usLf) {
        if (!!usLf.Width || f.Grouping !== 0) {
          usLf.Grouping = 0;
        } else {
          const i = userState.Fields.findIndex(y => y.Field.Id === f.Field.Id);
          userState.Fields.splice(i, 1);
        }
      } else {
        const lf = new LayoutField({Field: new Field({Id: f.Field.Id}), Grouping: 0, IsDescending: false});
        userState.Fields.push(lf);
      }
    });


    _groupedFieldsUpdate.forEach(f => {
      const colState = groupedColumns[f.Field.DefaultName];
      const usLf = userState.Fields.find(x => x.Field.Id === f.Field.Id);
      if (usLf) {
        if (f.Grouping !== colState.sortIndex || f.IsDescending !== (colState.groupSort === GroupSortType.desc ? true : false) || f.Grouping !== usLf.Grouping || f.IsDescending !== usLf?.IsDescending) {
          usLf.Grouping = colState.sortIndex;
          usLf.Sorting = 0;
          usLf.IsDescending = (colState.groupSort === GroupSortType.desc ? true : false);
        }
      } else {
        if (f.Grouping !== colState.sortIndex || f.IsDescending !== (colState.groupSort === GroupSortType.desc ? true : false)) {
          const lf = new LayoutField({
            Field: new Field({Id: f.Field.Id}),
            Grouping: colState.sortIndex,
            IsDescending: (colState.groupSort === GroupSortType.desc ? true : false)
          });
          userState.Fields.push(lf);
        }
      }
    });

    _groupedFieldsAdd.forEach(defaultName => {
      const colState = groupedColumns[defaultName];
      const field = (this.layoutState?.layout.Fields ?? []).find(x => x.Field.DefaultName === defaultName);
      const usLf = userState.Fields.find(f => f.Field.Id === field.Field.Id);
      if (usLf) {
        usLf.Grouping = colState.sortIndex;
        usLf.Sorting = 0;
        usLf.IsDescending = (colState.groupSort === GroupSortType.desc ? true : false);
      } else {
        userState.Fields.push(new LayoutField({
          Field: new Field({Id: field.Field.Id}),
          Grouping: colState.sortIndex,
          IsDescending: (colState.groupSort === GroupSortType.desc ? true : false)
        }));
      }
    });

    if (JSON.stringify(this.layoutState.userState) != JSON.stringify(userState)) {
      // console.warn('### userState ###');
      // console.warn(userState);

      if ((userState?.Fields?.length ?? 0) === 0) {
        delete this.layoutState.userState;
      }
      else {
        this.layoutState.userState = userState;
      }

      await this.layoutStateService.setLayoutState(this.layoutState.userState);
    }
    this.applyGridSorting();
  }

  async getSortItems() {

    const fieldsDict: { [id: number]: Field } = {};
    (this.layoutState?.layout?.Fields ?? []).map(f => fieldsDict[f.Field.Id] = f.Field);

    const tempSortItems: { Field: Field, Direction: SortDirectionEnum, IsGroupSorting: boolean, sorting?: number, grouping?: number }[] = [];
    const layoutGroupingFields = (this.layoutState?.layout?.Fields?.filter(x => x.Grouping !== 0) ?? []);
    const layoutSortingFields = (this.layoutState?.layout?.Fields?.filter(x => x.Sorting !== 0) ?? []);

    for (const group of layoutGroupingFields) {
      const f = this.layoutState?.userState?.Fields?.find(x => fieldsDict.hasOwnProperty(x.Field.Id) && fieldsDict[x.Field.Id].DefaultName === group.Field?.DefaultName);
      if (f) {
        if ((f.Grouping ?? 0) !== 0) {
          tempSortItems.push({
            Field: fieldsDict[f.Field.Id],
            Direction: (f.IsDescending ? SortDirectionEnum.Descending : SortDirectionEnum.Ascending),
            IsGroupSorting: true,
            grouping: f.Grouping
          });
        }
      } else {
        const field = (this.layoutState?.layout.Fields ?? []).find(x => x.Field.DefaultName === group.Field?.DefaultName);
        tempSortItems.push({
          Field: fieldsDict[field.Field.Id],
          Direction: (field.IsDescending ? SortDirectionEnum.Descending : SortDirectionEnum.Ascending),
          IsGroupSorting: true,
          grouping: field.Grouping
        });
      }
    }

    for (const field of (this.layoutState?.userState?.Fields ?? [])) {
      const g = layoutGroupingFields.find(x => field.Field.Id === x.Field.Id);
      if (!g && (field.Grouping ?? 0) !== 0) {
        tempSortItems.push({
          Field: fieldsDict[field.Field.Id],
          Direction: (field.IsDescending ? SortDirectionEnum.Descending : SortDirectionEnum.Ascending),
          IsGroupSorting: true,
          grouping: field.Grouping
        });
      }
    }


    for (const sortItem of layoutSortingFields) {
      const f = this.layoutState?.userState?.Fields?.find(x => fieldsDict.hasOwnProperty(x.Field.Id) && fieldsDict[x.Field.Id].DefaultName === sortItem.Field?.DefaultName);
      if (f) {
        if ((f.Sorting ?? 0) !== 0) {
          tempSortItems.push({
            Field: fieldsDict[f.Field.Id],
            Direction: (f.IsDescending ? SortDirectionEnum.Descending : SortDirectionEnum.Ascending),
            IsGroupSorting: false,
            sorting: f.Sorting
          });
        }
      } else {
        const field = (this.layoutState?.layout.Fields ?? []).find(x => x.Field.DefaultName === sortItem.Field.DefaultName);
        tempSortItems.push({
          Field: fieldsDict[field.Field.Id],
          Direction: (field.IsDescending ? SortDirectionEnum.Descending : SortDirectionEnum.Ascending),
          IsGroupSorting: false,
          sorting: field.Sorting
        });
      }
    }

    for (const field of (this.layoutState?.userState?.Fields ?? [])) {
      const s = layoutSortingFields.find(x => field.Field.Id === x.Field.Id);
      // const s = this.layoutState?.sortItems.find(x => fieldsDict[field.Field.Id].DefaultName === x.Field.DefaultName);
      if (!s && (field.Sorting ?? 0) !== 0) {
        tempSortItems.push({
          Field: fieldsDict[field.Field.Id],
          Direction: (field.IsDescending ? SortDirectionEnum.Descending : SortDirectionEnum.Ascending),
          IsGroupSorting: false,
          sorting: field.Sorting
        });
      }
    }

    const sortItems = tempSortItems.sort((a, b) =>
      (!!a.grouping ? 0 : 1) - (!!b.grouping ? 0 : 1)
      || a.grouping - b.grouping
      || a.sorting - b.sorting
    ).map(m => new LayoutSortItem(({Field: m.Field, Direction: m.Direction, IsGroupSorting: m.IsGroupSorting})));

    return {
      LayoutSortItems: sortItems,
      SortingSource: ((this.layoutState?.userState?.Fields?.length ?? 0) == 0 ? FaultSortingSourceEnum.Layout : FaultSortingSourceEnum.Passed)
    };
  }

  public async applyLayoutState(newState: LayoutStateModel) {
    this.layoutState = newState;
    const colDefs = await this.getColDefs(newState);
    this.currentColDefs = colDefs;

    // console.warn('### colDefs ###');
    // console.warn(colDefs);

    if (this.gridOptions && this.gridOptions.api) {
      this.gridOptions.api.setColumnDefs(colDefs);
      this.gridOptions.columnApi.resetColumnState();
    } else if (this.gridOptions) {
      this.gridOptions.columnDefs = colDefs;
    }
  }

  public async getColDefs(layoutState: LayoutStateModel): Promise<ColDef[]> {
    const userStateLog: { [defaultName: string]: number } = {};


    this.imageColumnActive = false;

    const fields = layoutState && layoutState.layout && layoutState.layout.Fields || [];
    const cols: ColDef[] = [];

    cols.push(
      {
        colId: 'syncState',
        lockPosition: true,
        width: 6,
        minWidth: 6,
        maxWidth: 6,
        resizable: false,
        pinned: true,
        suppressAutoSize: true,
        suppressSizeToFit: true,
        suppressMovable: true,
        filter: false,
        field: 'modelState$',
        cellRenderer: SyncCellRendererComponent,
        // headerComponent: SelectionColHeaderComponent,
      },
    );

    cols.push(
      {
        colId: 'status',
        lockPosition: true,
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        resizable: false,
        pinned: true,
        suppressAutoSize: true,
        suppressSizeToFit: true,
        suppressMovable: true,
        filter: false,
        field: 'isChecked',
        cellRenderer: SelectionCheckboxCellRenderer,
        headerComponent: SelectionCheckboxHeaderCellRenderer,
      },
    );

    if (fields && fields.length > 0) {

      const sortedColumns: FieldOrder[] = layoutState.userColumnOrder;
      const userStates = this.getUserStates(layoutState);

      const fieldsDict =  Object.fromEntries(fields.map(f => [f.Field.Id, f]));

      let delaysDict: { [id: number]: any } = {};
      const anyDelayGroupIdField = fields?.some(p => p.Field.DefaultName.startsWith('DelayGroup') && p.Field.DefaultName.endsWith('Id'));
      if (anyDelayGroupIdField) {
        const delays = await firstValueFrom(this.projectLoaderService.delays.pipe(filter(userPerm => userPerm.loadState.status == LoadStatusEnum.Loaded), map(m => m.data)));
        delaysDict = Object.fromEntries(delays.map(x => [+x.Id, x]));
      }

      if (sortedColumns && this.checkIfLayoutIndexState(fields, sortedColumns)) {
        sortedColumns.forEach(p => {
          const f = fieldsDict[p.Field.Id];

          // const width = fieldDefaults.hasOwnProperty(f.Field.DefaultName) && fieldDefaults[f.Field.DefaultName].defaultWidth > userStates[f.Field.DefaultName].width ? fieldDefaults[f.Field.DefaultName].defaultWidth : userStates[f.Field.DefaultName].width;
          const width = userStates[f.Field.DefaultName].width;
          const pinned = userStates[f.Field.DefaultName].pinned;

          let defColDef: MydocmaColumnDefinition = {
            colId: f.Field.DefaultName,
            headerName: f.Field.GlobalName,
            headerTooltip: f.Field.GlobalName,
            field: 'layoutState.data.' + f.Field.DefaultName,
            width,
            pinned,
            comparator: () => 0,
            valueFormatter: (params) => this.getFieldValueFormatter(params, f),
            filterValueGetter: (params) => ' ',
            suppressComparatorForGroupSort: true
          };
          if (f.Field.DefaultName === 'Number2' || f.Field.DefaultName === 'Number') {
            defColDef.disableDrop = true;
          }

          if (f.Field.DefaultName.endsWith('Image') || f.Field.DefaultName.endsWith('PlanSection')) {
            defColDef.minWidth = colTypeMinWidth[FieldDataTypeEnum.Image];
            defColDef.disableDrop = true;
          } else if (f.Field.DefaultName.startsWith('DelayGroup') && f.Field.DefaultName.endsWith('Id')) {
            defColDef.minWidth = colTypeMinWidth[100];
            defColDef.valueFormatter = (params) => this.getDelayValueFormatter(params, f, delaysDict);
          } else if (colTypeMinWidth.hasOwnProperty(f.Field.DataType)) {
            defColDef.minWidth = colTypeMinWidth[f.Field.DataType];
          }

          userStateLog[f.Field.DefaultName] = userStates[f.Field.DefaultName].width;

          if (userStates[f.Field.DefaultName].sortIndex != undefined) {
            defColDef.sortIndex = userStates[f.Field.DefaultName].sortIndex;
          }

          if (userStates[f.Field.DefaultName].sort != undefined) {
            defColDef.sort = userStates[f.Field.DefaultName].sort;
          }


          const typeColDef = this.getTypeColDef(f);
          if (f && typeColDef !== null) {
            defColDef = deepMerge(defColDef, typeColDef);
          }
          defColDef.lockPinned = false;
          cols.push(defColDef);

        });
      } else {
        fields.forEach(f => {
          // const width = fieldDefaults.hasOwnProperty(f.Field.DefaultName) && fieldDefaults[f.Field.DefaultName].defaultWidth > userStates[f.Field.DefaultName].width ? fieldDefaults[f.Field.DefaultName].defaultWidth : userStates[f.Field.DefaultName].width;
          const width = userStates[f.Field.DefaultName].width;

          let defColDef: MydocmaColumnDefinition = {
            colId: f.Field.DefaultName,
            headerName: f.Field.GlobalName,
            headerTooltip: f.Field.GlobalName,
            field: 'layoutState.data.' + f.Field.DefaultName,
            width,
            comparator: () => 0,
            valueFormatter: (params) => this.getFieldValueFormatter(params, f),
            filterValueGetter: (params) => ' ',
          };


          if (f.Field.DefaultName.endsWith('Image') || f.Field.DefaultName.endsWith('PlanSection')) {

            defColDef.minWidth = colTypeMinWidth[FieldDataTypeEnum.Image];
            defColDef.disableDrop = true;
          } else if (f.Field.DefaultName.startsWith('DelayGroup') && f.Field.DefaultName.endsWith('Id')) {
            defColDef.minWidth = colTypeMinWidth[100];
            defColDef.valueFormatter = (params) => this.getDelayValueFormatter(params, f, delaysDict);
          } else if (colTypeMinWidth.hasOwnProperty(f.Field.DataType)) {
            defColDef.minWidth = colTypeMinWidth[f.Field.DataType];
          }

          userStateLog[f.Field.DefaultName] = userStates[f.Field.DefaultName].width;

          if (userStates[f.Field.DefaultName].sortIndex != undefined) {
            defColDef.sortIndex = userStates[f.Field.DefaultName].sortIndex;
          }
          if (f.Field.DefaultName === 'Number2' || f.Field.DefaultName === 'Number') {
            defColDef.disableDrop = true;
          }

          if (userStates[f.Field.DefaultName].sort != undefined) {
            defColDef.sort = userStates[f.Field.DefaultName].sort;
          }

          const typeColDef = this.getTypeColDef(f);
          if (f && typeColDef !== null) {
            defColDef = deepMerge(defColDef, typeColDef);
          }

          defColDef.lockPinned = false;
          cols.push(defColDef);
        });
        this.layoutStateService.deleteColumnOrder();
      }
    }

    // console.warn('userStateLog');
    // console.warn(userStateLog);

    return cols;
  }

  private async onColumnSorted(event: SortChangedEvent) {
    console.log('[ColDefsService] onColumnSorted called:', { source: event?.source, sortingService: !!this.sortingService, columnApi: !!this.gridOptions?.columnApi });

    if (event && event.source === 'uiColumnSorted') {
      await this.updateUserState(undefined);

      if (this.sortingService && this.gridOptions?.columnApi) {
        const columnStates = this.gridOptions.columnApi.getColumnState();
        const sortedColumns = columnStates.filter(c => c.sort);
        console.log('[ColDefsService] Grid sort changed, syncing to tile. Sorted columns:', sortedColumns);
        this.sortingService.syncFromGridSort(columnStates);
      } else {
        console.warn('[ColDefsService] Cannot sync to tile - sortingService or columnApi not available');
      }
    }
  }

  private async applyGridSorting() {
    const sortItems = await this.getSortItems();
    this.layoutStateService.setSorting(sortItems.LayoutSortItems);
  }

  public async updateAllSortedColumnsDirection(direction: 'asc' | 'desc'): Promise<void> {
    if (!this.gridOptions?.columnApi) {
      console.warn('[ColDefsService] Cannot update sort direction - no columnApi available');
      return;
    }

    const columnStates = this.gridOptions.columnApi.getColumnState();
    const sortedColumns = columnStates.filter(col => col.sort != null);

    if (sortedColumns.length === 0) {
      console.warn('[ColDefsService] No sorted columns found');
      return;
    }


    sortedColumns.forEach(col => {
      col.sort = direction;
    });

    this.gridOptions.columnApi.applyColumnState({ state: columnStates });

    await this.updateUserState(undefined);
  }

  private async onColumnResized(event) {
    if (event.finished && event.source === 'uiColumnDragged') {
      console.debug('Column resized: ' + event.column.colDef.colId + ' width ' + event.column.actualWidth + ' type ' + event.type);

      const field = this.layoutState.layout.Fields.find(lf => lf.Field && lf.Field.DefaultName === event.column.colDef.colId);
      if (field) {
        let userField = this.layoutState.userState?.Fields && this.layoutState.userState.Fields.find(lf => lf.Field && lf.Field.Id === field.Field.Id);
        if (userField) {
          if (userField.Width !== event.column.actualWidth) {
            userField.Width = event.column.actualWidth;
            await this.layoutStateService.setLayoutState(this.layoutState.userState);
          }
          // if (userFiled.SortId != event.column)
        } else {
          userField = new LayoutField({Width: event.column.actualWidth, Field: {Id: field.Field.Id}});
          if (!this.layoutState.userState) {
            this.layoutState.userState = new Layout();
          }
          this.layoutState.userState.Fields = this.layoutState.userState.Fields || [];
          this.layoutState.userState.Fields.push(userField);
          await this.layoutStateService.setLayoutState(this.layoutState.userState);
        }
      }

      await timer(1000).toPromise();
    }
  }

  private async onColumnDragged(event: DragStoppedEvent) {
    const elem: FieldOrder[] = [];

    const colums: ColDef[] = event?.api?.getColumnDefs();
    for (let k = 2; k < colums.length; k++) {
      const field = this.layoutState?.layout?.Fields.find(lf => lf.Field && lf.Field.DefaultName === colums[k]?.colId);
      if (field) {
        elem.push({Field: {Id: field.Field.Id}, Index: k, Pinned: colums[k].pinned});
      }
    }

    this.currentColDefs = colums;
    await this.layoutStateService.setLayoutColumnOrder(elem);

    if (this.gridOptions && this.gridOptions.api) {
      this.gridOptions.api.setColumnDefs(this.currentColDefs);
      this.gridOptions.columnApi.resetColumnState();
    }
  }

  private checkIfLayoutIndexState(fields: LayoutField[], orderColumns: FieldOrder[]) {
    return (fields.length === orderColumns.length) && (fields.every(field => orderColumns.find(sorted => field.Field.Id === sorted.Field.Id)));
  }

  private getUserStates(layoutState: LayoutStateModel): { [defaultName: string]: ({ width: number, sortIndex: number, sort: 'asc' | 'desc', pinned: boolean | 'left' | 'right' | undefined}) } {
    let width: number;
    let sortIndex: number;
    let sort: 'asc' | 'desc';
    let pinned: boolean | 'left' | 'right' | undefined;

    const userStateFields = Object.fromEntries((this.layoutState?.userState?.Fields ?? []).map(x => [x.Field.Id, x]));
    const columnOrders = Object.fromEntries((this.layoutState?.userColumnOrder ?? []).map(x => [x.Field.Id, x]));

    const userStates: { [defaultName: string]:
                        ({ width: number, sortIndex: number, sort: 'asc' | 'desc', pinned: boolean | 'left' | 'right' | undefined })
                      } = {};

    layoutState.layout.Fields.map(lf => {

      if (userStateFields.hasOwnProperty(lf.Field.Id) && userStateFields[lf.Field.Id].Width != null) {
        width = userStateFields[lf.Field.Id].Width;
      } else {
        if (lf.Field.DataType === FieldDataTypeEnum.Boolean) {
          width = 70;
        } else {
          width = (lf.Width / 10);
        }
      }

      if (userStateFields.hasOwnProperty(lf.Field.Id) && userStateFields[lf.Field.Id].Sorting != null && userStateFields[lf.Field.Id].IsDescending != null) {
        if (userStateFields[lf.Field.Id].Sorting === 0) {
          sortIndex = undefined;
          sort = undefined;
        } else {
          sortIndex = (userStateFields[lf.Field.Id].Sorting - 1);
          sort = (userStateFields[lf.Field.Id].IsDescending ? 'desc' : 'asc');
        }
      } else {
        if (lf.Sorting !== 0) {
          sortIndex = (lf.Sorting - 1);
          sort = (lf.IsDescending ? 'desc' : 'asc');
        } else {
          sortIndex = undefined;
          sort = undefined;
        }
      }

      if (columnOrders?.hasOwnProperty(lf.Field.Id)) {
        pinned = columnOrders[lf.Field.Id].Pinned;
      }

      userStates[lf.Field.DefaultName] = {width, sortIndex, sort, pinned};
    });
    return userStates;
  }

  private getTypeColDef(field: LayoutField): ColDef {

    switch (field.Field.DataType) {
      case FieldDataTypeEnum.String:
        if (field.Field.DefaultName.includes('ResponsibleCT_Salutation')
          || field.Field.DefaultName.includes('ResponsibleCO_Salutation')
          || field.Field.DefaultName.includes('ResponsibleSC_Salutation')) {
          return SalutationColDef;
        } else if (field.Field.DefaultName === 'AttributeGroup_Image') {
          this.imageColumnActive = true;
          return AttributeGroupColDef;
        }
        return TextColDef;
      case FieldDataTypeEnum.Int32:
      case FieldDataTypeEnum.Int16:
      case FieldDataTypeEnum.Decimal:
      case FieldDataTypeEnum.Double:
        if (field.Field.DefaultName === 'LastImage' || field.Field.DefaultName === 'FirstImage' ||
          field.Field.DefaultName === 'LastPlanSection' || field.Field.DefaultName === 'FirstPlanSection') {
          this.imageColumnActive = true;

          const r = ImageColDef;
          r.tooltipField = 'layoutState.data.' + field.Field.DefaultName;
          return r;
        } else if (field.Field.DefaultName === 'DelayGroupCT_Id' || field.Field.DefaultName === 'DelayGroupSC_Id') {
          return DelayColDef;
        } else if (field.Field.DefaultName.includes('_StatusGroup')) {
          return StatusGroupColDef;
        } else if (field.Field.DefaultName.endsWith('_Count')) {
          return PositiveIntegerColDef;
        } else if (field.Field.DefaultName == 'Number') {
          const colDef = NumberColDef;
          colDef.sortingOrder = ['desc', 'asc', null];
          return colDef;
        } else if (field.Field.DefaultName.startsWith('Costs')) {
          const colDefCosts = NumberColDef;
          colDefCosts.type = 'Costs';
          return colDefCosts;
        } else {
          return NumberColDef;
        }
      case FieldDataTypeEnum.DateTime:
        return DateColDef;
      case FieldDataTypeEnum.Boolean:
        return BooleanColDef;
      case FieldDataTypeEnum.Time:
        return TimeColDef;
      default:
        return null;
    }
  }

  private getFieldValueFormatter(params: ValueFormatterParams, field: LayoutField) {
    switch (field.Field.DataType) {
      case FieldDataTypeEnum.DateTime:
        return this.datepipe.transform(params.value);

      case FieldDataTypeEnum.Decimal:
      case FieldDataTypeEnum.Double:
        if (field.Field.DefaultName !== FieldDefaultNameEnum.Number) {
          if (params.value) {
            const thousandsSeparator = getLocaleNumberSymbol(this.translateService.currentLang, NumberSymbol.CurrencyGroup);
            return this.numberpipe.transform(params.value)?.replace(new RegExp(`\\${thousandsSeparator}`, 'g'), '');
          }
        }
    }


    if (params?.colDef?.type === 'StatusGroup' && params?.value) {
      return this.translateService.instant('MM.statusGroup.' + params.value);
    }

    if (params?.colDef?.type === 'Salutation' && params?.value) {
      return this.translateService.instant('MM.salutation.' + params.value);
    }

    return params.value;
  }

  private getDelayValueFormatter(params: ValueFormatterParams, field: LayoutField, delaysDict: { [id: number]: any }) {

    if (params.value != null && field.Field.DefaultName.startsWith('DelayGroup') && field.Field.DefaultName.endsWith('Id')) {
      return (delaysDict.hasOwnProperty(params.value) ? delaysDict[params.value].Name2 : null);
    }
    return params.value;
  }
}
